import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import {
  AccountApiError,
  DEFAULT_ACCOUNT_TRIP_FILTERS,
  LegacyAccountApi,
  LegacyMapapalApi,
  MockAccountApi,
  MockMapapalApi,
  SharedTripApiError,
  clearAuthSession,
  parseShareLink,
  readAuthSession,
  readStoredShareLink,
  storeAuthSession,
  storeShareLink,
  stripShareParams,
  type AccountApi,
  type AccountTripFilters,
  type AccountTrip,
  type AccountTripUpdate,
  type AuthSession,
  type LoginCredentials,
  type RegistrationDetails,
  type ShareLink,
  type SharedTripApi,
  type TripDetails,
} from '../api'
import { ProfileDialog, SettingsDialog, SiteFooter, TripActionsMenu } from '../components'
import { AccountLoadingView, EditTripDialog, ForgotPasswordView, LoginView, RegisterView, ShareTripDialog, TripListView } from '../features/account'
import { SharedTripView, type SharedTripState } from '../features/shared-trip'
import { PublicInformationPage } from '../features/public-info/PublicInformationPage'
import { homeHref, publicPageFromPath, publicPageHref, type PublicPageName } from './publicRoutes'
import { formatTripStarted } from '../features/account/tripStarted'
import {
  formatDistance,
  formatElevation,
  formatSpeed,
  readFollowLatestPosition,
  readShowLivePositionInfo,
  readUnitSystem,
  storeFollowLatestPosition,
  storeShowLivePositionInfo,
  storeUnitSystem,
  type UnitSystem,
} from '../preferences'
import { ElevationProfile, elevationAtTripProgress } from './ElevationProfile'
import { GoogleTripMap } from './GoogleTripMap'
import { DesktopOverviewMap } from './DesktopOverviewMap'
import type { MapViewport } from './mapViewport'
import { TRIP_COMPARISON_COLORS } from './tripComparison'
import { googleMapsDirectionsUrl } from './viewerPosition'
import { applyTripListRefresh, includesNewTrip } from './accountTripList'
import { saveTripCsv } from '../tripCsvDownload'
import newTripSoundUrl from '../assets/mapapal-plopp.mp3'
import {
  LIVE_TRIP_MAX_POLL_INTERVAL_MS,
  LIVE_TRIP_POLL_INTERVAL_MS,
  latestPositionId,
  mergeTripRefresh,
  nextLiveTripPollInterval,
} from './liveTrip'
import './app.css'

function formatDate(value?: string): string {
  if (!value) return ''
  const normalized = value.includes('T') ? value : `${value.replace(' ', 'T')}Z`
  const date = new Date(normalized)
  return Number.isNaN(date.valueOf())
    ? value
    : new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short' }).format(date)
}

function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remaining = Math.floor(seconds % 60)
  return [hours, minutes, remaining].map((part) => String(part).padStart(2, '0')).join(':')
}

function formatTripPlatform(platform?: string): string | undefined {
  const value = platform?.trim()
  if (!value) return undefined
  const normalized = value.toLocaleLowerCase()
  if (normalized.includes('ios') || normalized.includes('iphone') || normalized.includes('ipad')) return 'iOS'
  if (normalized.includes('android')) return 'Android'
  return value
}

function currentElevation(positions: TripDetails['positions'], progress: number, unitSystem: UnitSystem): string | undefined {
  const elevation = elevationAtTripProgress(positions, progress)
  return elevation === undefined ? undefined : formatElevation(elevation, unitSystem)
}

function toPresentation(trip: TripDetails, unitSystem: UnitSystem): Extract<SharedTripState, { kind: 'ready' }>['trip'] {
  const altitudes = trip.positions
    .map((position) => position.altitudeMeters)
    .filter((altitude): altitude is number => altitude !== undefined)
  const visibility = {
    private: 'Private',
    pals: 'Shared with pals',
    public: 'Public',
    unknown: undefined,
  }[trip.visibility]
  const started = formatTripStarted(trip.startedAt)

  return {
    id: trip.id,
    title: trip.description || 'Untitled trip',
    isUntitled: !trip.description,
    ownerName: trip.ownerName,
    description: visibility,
    statusLabel: trip.status === 'ended' ? 'Completed' : trip.status === 'paused' ? 'Paused' : 'In progress',
    status: trip.status,
    visibilityLabel: visibility,
    startedAt: formatDate(trip.startedAt),
    startedLabel: started.label,
    startedDetail: started.detail || undefined,
    startedTitle: started.title,
    endedAt: trip.endedAt ? formatDate(trip.endedAt) : undefined,
    duration: formatDuration(trip.durationSeconds),
    distance: formatDistance(trip.distanceKilometers, unitSystem),
    averageSpeed: formatSpeed(trip.averageSpeedKilometersPerHour, unitSystem),
    platform: formatTripPlatform(trip.platform),
    views: trip.views,
    elevation: altitudes.length ? {
      minimum: formatElevation(Math.min(...altitudes), unitSystem),
      maximum: formatElevation(Math.max(...altitudes), unitSystem),
    } : undefined,
  }
}

function initialShareLink(): ShareLink | null {
  const fromUrl = parseShareLink(window.location.search)
  if (fromUrl) {
    storeShareLink(fromUrl)
    window.history.replaceState(
      { ...window.history.state, mapapalSharedTrip: true },
      '',
      stripShareParams(new URL(window.location.href)),
    )
    return fromUrl
  }
  const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
  const isRestoredNavigation = navigation?.type === 'reload' || navigation?.type === 'back_forward'
  return isRestoredNavigation && window.history.state?.mapapalSharedTrip === true
    ? readStoredShareLink()
    : null
}

function createSharedTripApi(): SharedTripApi {
  return import.meta.env.VITE_API_MODE === 'legacy'
    ? new LegacyMapapalApi({ baseUrl: import.meta.env.VITE_API_BASE_URL })
    : new MockMapapalApi()
}

function createAccountApi(): AccountApi {
  return import.meta.env.VITE_API_MODE === 'legacy'
    ? new LegacyAccountApi({ baseUrl: import.meta.env.VITE_API_BASE_URL })
    : new MockAccountApi()
}

function useDesktopTripWorkspace(): boolean {
  const [desktop, setDesktop] = useState(() => typeof window.matchMedia === 'function'
    && window.matchMedia('(min-width: 56rem)').matches)
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return
    const media = window.matchMedia('(min-width: 56rem)')
    const update = () => setDesktop(media.matches)
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [])
  return desktop
}

function openDirectionsToLatestPosition(positions: TripDetails['positions']): void {
  const target = positions.at(-1)
  if (!target) return
  window.open(googleMapsDirectionsUrl(target), '_blank', 'noopener,noreferrer')
}

function comparisonStatusLabel(status: AccountTrip['status']): string {
  if (status === 'running') return 'In progress'
  if (status === 'paused') return 'Paused'
  return 'Completed'
}

function TripComparisonOverlay({
  trips,
  loading,
  onClear,
  onReplay,
  onRemove,
}: {
  trips: AccountTrip[]
  loading: boolean
  onClear: () => void
  onReplay: (tripId: string) => void
  onRemove: (trip: AccountTrip) => void
}) {
  const panelRef = useRef<HTMLDivElement | null>(null)
  const dragRef = useRef<{ pointerId: number; startX: number; startOffset: number; minimum: number; maximum: number } | null>(null)
  const [offsetX, setOffsetX] = useState(0)
  const [visible, setVisible] = useState(true)
  const tripIds = trips.map((trip) => trip.id).join(',')

  useEffect(() => {
    setOffsetX(0)
    setVisible(true)
  }, [tripIds])

  const startDragging = (event: ReactPointerEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('button')) return
    const panel = panelRef.current
    const container = panel?.offsetParent as HTMLElement | null
    if (!panel || !container) return
    const panelBounds = panel.getBoundingClientRect()
    const containerBounds = container.getBoundingClientRect()
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startOffset: offsetX,
      minimum: offsetX - Math.max(0, panelBounds.left - containerBounds.left - 8),
      maximum: offsetX + Math.max(0, containerBounds.right - panelBounds.right - 8),
    }
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const drag = (event: ReactPointerEvent<HTMLDivElement>) => {
    const gesture = dragRef.current
    if (!gesture || gesture.pointerId !== event.pointerId) return
    setOffsetX(Math.min(gesture.maximum, Math.max(gesture.minimum, gesture.startOffset + event.clientX - gesture.startX)))
  }

  const stopDragging = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId !== event.pointerId) return
    dragRef.current = null
    event.currentTarget.releasePointerCapture(event.pointerId)
  }

  if (!visible) return null
  return (
    <div ref={panelRef} className="trip-comparison__panel" style={{ transform: `translateX(${offsetX}px)` }} data-testid="trip-comparison-view">
      <div className="trip-comparison__heading" onPointerDown={startDragging} onPointerMove={drag} onPointerUp={stopDragging} onPointerCancel={stopDragging}>
        <strong>{trips.length} selected</strong>
        <div className="trip-comparison__heading-actions">
          <button type="button" onClick={onClear}>Clear</button>
          <button className="trip-comparison__close" type="button" aria-label="Hide selected trips panel" title="Hide panel" onClick={() => setVisible(false)}>×</button>
        </div>
      </div>
      <ul className="trip-comparison__legend" aria-label="Route colours">
        {trips.map((trip, index) => (
          <li key={trip.id}>
            <i style={{ backgroundColor: TRIP_COMPARISON_COLORS[index % TRIP_COMPARISON_COLORS.length] }} aria-hidden="true" />
            <span><strong>{trip.description || 'Untitled trip'}</strong><small>{trip.ownerName} · {comparisonStatusLabel(trip.status)}</small></span>
            <div className="trip-comparison__route-actions">
              <button type="button" aria-label={`Replay ${trip.description || 'untitled trip'}`} title="Replay trip" onClick={() => onReplay(trip.id)}>↻</button>
              <button type="button" aria-label={`Remove ${trip.description || 'untitled trip'} from comparison map`} title="Remove trip" onClick={() => onRemove(trip)}>×</button>
            </div>
          </li>
        ))}
      </ul>
      {loading ? <p role="status">Adding route…</p> : null}
    </div>
  )
}

function SharedTripRoute({
  link,
  unitSystem,
  followLatestPosition,
  showLivePositionInfo,
  onOpenSettings,
}: {
  link: ShareLink
  unitSystem: UnitSystem
  followLatestPosition: boolean
  showLivePositionInfo: boolean
  onOpenSettings: () => void
}) {
  const desktopDetailsLayout = useDesktopTripWorkspace()
  const [trip, setTrip] = useState<TripDetails | null>(null)
  const [state, setState] = useState<SharedTripState>({ kind: 'loading' })
  const [attempt, setAttempt] = useState(0)
  const [playbackProgress, setPlaybackProgress] = useState(0)
  const [viewingLive, setViewingLive] = useState(true)
  const [showViewerPosition, setShowViewerPosition] = useState(false)
  const [session, setSession] = useState<AuthSession | null>(readAuthSession)
  const [profileOpen, setProfileOpen] = useState(false)
  const api = useMemo(createSharedTripApi, [])
  const accountApi = useMemo(createAccountApi, [])

  const goToHome = () => {
    window.history.replaceState({}, '', new URL(import.meta.env.BASE_URL, window.location.href))
    window.location.reload()
  }

  useEffect(() => {
    const controller = new AbortController()
    setTrip(null)
    setShowViewerPosition(false)
    setPlaybackProgress(0)
    setViewingLive(true)
    setState({ kind: 'loading' })
    void api.getSharedTrip(link, controller.signal).then((details) => {
      setTrip(details)
    }).catch((error: unknown) => {
      if (error instanceof DOMException && error.name === 'AbortError') return
      if (error instanceof SharedTripApiError) {
        if (error.code === 'access-denied') setState({ kind: 'access-denied' })
        else if (error.code === 'not-found-or-denied') setState({ kind: 'not-found' })
        else setState({ kind: 'error' })
      } else {
        setState({ kind: 'error' })
      }
    })
    return () => controller.abort()
  }, [api, attempt, link])

  useEffect(() => {
    if (trip) setState({ kind: 'ready', trip: toPresentation(trip, unitSystem) })
  }, [trip, unitSystem])

  useEffect(() => {
    if (!trip || trip.status === 'ended') return
    const controller = new AbortController()
    let active = true
    let timer: number | undefined
    let lastPositionId = latestPositionId(trip.positions)
    let pollInterval = LIVE_TRIP_POLL_INTERVAL_MS

    const poll = async () => {
      try {
        const refresh = await api.refreshSharedTrip(link, lastPositionId, controller.signal)
        if (!active) return
        lastPositionId = latestPositionId(refresh.positions, lastPositionId)
        setTrip((current) => current ? mergeTripRefresh(current, refresh) : current)
        if (refresh.status === 'ended') return
        pollInterval = nextLiveTripPollInterval(pollInterval, refresh.positions.length > 0)
      } catch (error) {
        if (!active || (error instanceof DOMException && error.name === 'AbortError')) return
        pollInterval = LIVE_TRIP_MAX_POLL_INTERVAL_MS
      }
      timer = window.setTimeout(poll, pollInterval)
    }

    timer = window.setTimeout(poll, LIVE_TRIP_POLL_INTERVAL_MS)
    return () => {
      active = false
      if (timer !== undefined) window.clearTimeout(timer)
      controller.abort()
    }
  }, [api, link, trip?.id, trip?.status])

  const logout = () => {
    clearAuthSession()
    setProfileOpen(false)
    setSession(null)
  }

  const elevationProgress = trip?.status !== 'ended' && viewingLive ? 1 : playbackProgress

  return (
    <>
      <SharedTripView
        state={state}
        desktopDetailsLayout={desktopDetailsLayout}
        mapSlot={trip ? (tripIdentity, timelineSideSlot) => <GoogleTripMap positions={trip.positions} status={trip.status} unitSystem={unitSystem} followLatestPosition={followLatestPosition} showLivePositionInfo={showLivePositionInfo} showViewerPosition={showViewerPosition} progress={playbackProgress} onProgressChange={setPlaybackProgress} onViewingLiveChange={setViewingLive} timelineInfoSlot={tripIdentity} timelineSideSlot={timelineSideSlot} fitAvailableHeight={desktopDetailsLayout} /> : undefined}
        elevationSlot={trip ? <ElevationProfile positions={trip.positions} progress={elevationProgress} unitSystem={unitSystem} /> : undefined}
        elevationCurrent={trip ? currentElevation(trip.positions, elevationProgress, unitSystem) : undefined}
        onDownloadCsv={trip ? () => saveTripCsv(trip.id, () => api.getSharedTripCsv(link)) : undefined}
        onOpenDirections={trip?.positions.length ? () => openDirectionsToLatestPosition(trip.positions) : undefined}
        onToggleViewerPosition={trip?.positions.length ? () => setShowViewerPosition((current) => !current) : undefined}
        viewerPositionActive={showViewerPosition}
        onRetry={() => setAttempt((value) => value + 1)}
        onBack={goToHome}
        onHome={goToHome}
        onOpenSettings={onOpenSettings}
        signedInUsername={session?.username}
        onOpenProfile={session ? () => setProfileOpen(true) : undefined}
      />
      {session ? (
        <ProfileDialog open={profileOpen} session={session} api={accountApi} onLogout={logout} onClose={() => setProfileOpen(false)} />
      ) : null}
    </>
  )
}

type AccountState =
  | { kind: 'signed-out'; error?: string }
  | { kind: 'checking' }
  | {
      kind: 'trips'
      session: AuthSession
      trips: AccountTrip[]
      loading: boolean
      loadingEarlier: boolean
      hasEarlier: boolean
      error?: string
      earlierError?: string
    }

const TRIP_PAGE_SIZE = 15
const TRIP_LIST_POLL_INTERVAL_MS = 10_000

function updateMetaContent(selector: string, content: string): void {
  document.querySelector<HTMLMetaElement>(selector)?.setAttribute('content', content)
}

function updateCanonicalUrl(url: string): void {
  document.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.setAttribute('href', url)
  updateMetaContent('meta[property="og:url"]', url)
}

const PUBLIC_PAGE_META: Record<PublicPageName, { title: string; description: string }> = {
  about: {
    title: 'About Mapapal – Live GPS trip sharing',
    description: 'Learn how Mapapal lets you record a journey, share live progress, control who can follow, and revisit every trip.',
  },
  privacy: {
    title: 'Privacy & cookies – Mapapal',
    description: 'Learn how Mapapal handles account, trip and location information, sharing choices, cookies and browser storage.',
  },
  terms: {
    title: 'Terms of use – Mapapal',
    description: 'Read the terms that apply when using Mapapal apps, trip pages and live GPS sharing services.',
  },
}

function scrollToPageTop(): void {
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
}

function messageForAccountError(error: unknown): string {
  if (error instanceof AccountApiError) {
    if (error.code === 'invalid-credentials') return 'The username or password was incorrect.'
    if (error.code === 'network') return 'Mapapal could not be reached. Check your connection and try again.'
    if (error.code === 'expired-session') return 'Your session has expired. Please sign in again.'
    if (error.code === 'invalid-response') return error.message
    if (error.code === 'request-rejected') return error.message
  }
  return 'Something went wrong. Please try again.'
}

function AccountTripRoute({
  api,
  session,
  trip,
  unitSystem,
  followLatestPosition,
  showLivePositionInfo,
  onBack,
  onOpenSettings,
  onOpenProfile,
  onEdit,
  comparisonTrips,
  onClearComparison,
  onRemoveComparisonTrip,
  onMapViewportChange,
  embedded = false,
}: {
  api: AccountApi
  session: AuthSession
  trip: AccountTrip
  unitSystem: UnitSystem
  followLatestPosition: boolean
  showLivePositionInfo: boolean
  onBack: () => void
  onOpenSettings: () => void
  onOpenProfile: () => void
  onEdit: () => void
  comparisonTrips?: AccountTrip[]
  onClearComparison?: () => void
  onRemoveComparisonTrip?: (trip: AccountTrip) => void
  onMapViewportChange?: (viewport: MapViewport) => void
  embedded?: boolean
}) {
  const [details, setDetails] = useState<TripDetails | null>(null)
  const [state, setState] = useState<SharedTripState>({ kind: 'loading' })
  const [attempt, setAttempt] = useState(0)
  const [playbackProgress, setPlaybackProgress] = useState(0)
  const [viewingLive, setViewingLive] = useState(true)
  const [showViewerPosition, setShowViewerPosition] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)
  const [comparisonDetails, setComparisonDetails] = useState<TripDetails[]>([])
  const [comparisonLoading, setComparisonLoading] = useState(false)
  const [comparisonReplayRuns, setComparisonReplayRuns] = useState<Record<string, number>>({})
  const comparisonDetailsRef = useRef<TripDetails[]>([])
  const ownTrip = trip.ownerName === session.username
  const selectedComparisonTrips = comparisonTrips ?? [trip]
  const comparisonMode = selectedComparisonTrips.length > 1
  const comparisonTripIds = selectedComparisonTrips.map((candidate) => candidate.id).join(',')

  useEffect(() => {
    comparisonDetailsRef.current = comparisonDetails
  }, [comparisonDetails])

  useEffect(() => {
    const controller = new AbortController()
    setDetails(null)
    setShowViewerPosition(false)
    setPlaybackProgress(0)
    setViewingLive(true)
    setState({ kind: 'loading' })
    void api.getTrip(session, trip, controller.signal).then((loadedTrip) => {
      setDetails(loadedTrip)
    }).catch((error: unknown) => {
      if (error instanceof DOMException && error.name === 'AbortError') return
      setState({ kind: 'error' })
    })
    return () => controller.abort()
  }, [api, attempt, session, trip.id])

  useEffect(() => {
    if (details) setState({ kind: 'ready', trip: toPresentation(details, unitSystem) })
  }, [details, unitSystem])

  useEffect(() => {
    if (!details || details.status === 'ended') return
    const controller = new AbortController()
    let active = true
    let timer: number | undefined
    let lastPositionId = latestPositionId(details.positions)
    let pollInterval = LIVE_TRIP_POLL_INTERVAL_MS

    const poll = async () => {
      try {
        const refresh = await api.refreshTrip(session, details.id, lastPositionId, controller.signal)
        if (!active) return
        lastPositionId = latestPositionId(refresh.positions, lastPositionId)
        setDetails((current) => current ? mergeTripRefresh(current, refresh) : current)
        if (refresh.status === 'ended') return
        pollInterval = nextLiveTripPollInterval(pollInterval, refresh.positions.length > 0)
      } catch (error) {
        if (!active || (error instanceof DOMException && error.name === 'AbortError')) return
        pollInterval = LIVE_TRIP_MAX_POLL_INTERVAL_MS
      }
      timer = window.setTimeout(poll, pollInterval)
    }

    timer = window.setTimeout(poll, LIVE_TRIP_POLL_INTERVAL_MS)
    return () => {
      active = false
      if (timer !== undefined) window.clearTimeout(timer)
      controller.abort()
    }
  }, [api, details?.id, details?.status, session])

  useEffect(() => {
    const controller = new AbortController()
    const extraTrips = selectedComparisonTrips.filter((candidate) => candidate.id !== trip.id)
    const extraIds = new Set(extraTrips.map((candidate) => candidate.id))
    const retained = comparisonDetailsRef.current.filter((candidate) => extraIds.has(candidate.id))
    const retainedIds = new Set(retained.map((candidate) => candidate.id))
    const missingTrips = extraTrips.filter((candidate) => !retainedIds.has(candidate.id))
    setComparisonDetails(retained)
    setComparisonLoading(missingTrips.length > 0)
    if (!missingTrips.length) return () => controller.abort()

    void Promise.allSettled(missingTrips.map((candidate) => api.getTrip(session, candidate, controller.signal))).then((results) => {
      if (controller.signal.aborted) return
      const loaded = results.flatMap((result) => result.status === 'fulfilled' ? [result.value] : [])
      setComparisonDetails((current) => extraTrips.flatMap((candidate) => {
        const match = current.find((loadedTrip) => loadedTrip.id === candidate.id)
          ?? loaded.find((loadedTrip) => loadedTrip.id === candidate.id)
        return match ? [match] : []
      }))
      setComparisonLoading(false)
    })
    return () => controller.abort()
  }, [api, comparisonTripIds, session, trip.id])

  useEffect(() => {
    if (!comparisonMode) return
    const controller = new AbortController()
    let active = true
    let timer: number | undefined

    const poll = async () => {
      const liveTrips = comparisonDetailsRef.current.filter((candidate) => candidate.status !== 'ended')
      if (liveTrips.length) {
        const results = await Promise.allSettled(liveTrips.map((candidate) => (
          api.refreshTrip(session, candidate.id, latestPositionId(candidate.positions), controller.signal)
        )))
        if (!active) return
        const refreshes = new Map<string, TripDetails>()
        results.forEach((result, index) => {
          if (result.status === 'fulfilled') refreshes.set(liveTrips[index]!.id, result.value)
        })
        if (refreshes.size) {
          setComparisonDetails((current) => current.map((candidate) => {
            const refresh = refreshes.get(candidate.id)
            return refresh ? mergeTripRefresh(candidate, refresh) : candidate
          }))
        }
      }
      timer = window.setTimeout(poll, LIVE_TRIP_POLL_INTERVAL_MS)
    }

    timer = window.setTimeout(poll, LIVE_TRIP_POLL_INTERVAL_MS)
    return () => {
      active = false
      if (timer !== undefined) window.clearTimeout(timer)
      controller.abort()
    }
  }, [api, comparisonMode, comparisonTripIds, session])

  const elevationProgress = details?.status !== 'ended' && viewingLive ? 1 : playbackProgress
  const comparisonRoutes = selectedComparisonTrips.flatMap((candidate, index) => {
    const routeDetails = candidate.id === details?.id
      ? details
      : comparisonDetails.find((loadedTrip) => loadedTrip.id === candidate.id)
    return routeDetails ? [{
      id: candidate.id,
      positions: routeDetails.positions,
      status: routeDetails.status,
      color: TRIP_COMPARISON_COLORS[index % TRIP_COMPARISON_COLORS.length],
      replayRun: comparisonReplayRuns[candidate.id] ?? 0,
    }] : []
  })

  return (
    <>
      <SharedTripView
        state={state}
        embedded={embedded}
        comparisonMode={comparisonMode}
        mapSlot={details ? (tripIdentity, timelineSideSlot) => <GoogleTripMap positions={details.positions} status={details.status} unitSystem={unitSystem} followLatestPosition={followLatestPosition} showLivePositionInfo={showLivePositionInfo} showViewerPosition={showViewerPosition} progress={playbackProgress} onProgressChange={setPlaybackProgress} onViewingLiveChange={setViewingLive} timelineInfoSlot={tripIdentity} timelineSideSlot={timelineSideSlot} fitAvailableHeight={embedded} comparisonMode={comparisonMode} comparisonRoutes={comparisonRoutes} comparisonOverlaySlot={comparisonMode && onClearComparison && onRemoveComparisonTrip ? <TripComparisonOverlay trips={selectedComparisonTrips} loading={comparisonLoading} onClear={onClearComparison} onRemove={onRemoveComparisonTrip} onReplay={(tripId) => setComparisonReplayRuns((current) => ({ ...current, [tripId]: (current[tripId] ?? 0) + 1 }))} /> : undefined} onViewportChange={onMapViewportChange} /> : undefined}
        elevationSlot={details ? <ElevationProfile positions={details.positions} progress={elevationProgress} unitSystem={unitSystem} /> : undefined}
        elevationCurrent={details ? currentElevation(details.positions, elevationProgress, unitSystem) : undefined}
        onDownloadCsv={details ? () => saveTripCsv(details.id, () => api.getTripCsv(session, details.id)) : undefined}
        tripActions={ownTrip ? <TripActionsMenu label={trip.description || 'untitled trip'} onShare={() => setShareOpen(true)} onEdit={onEdit} /> : undefined}
        onOpenDirections={details?.positions.length ? () => openDirectionsToLatestPosition(details.positions) : undefined}
        onToggleViewerPosition={details?.positions.length ? () => setShowViewerPosition((current) => !current) : undefined}
        viewerPositionActive={showViewerPosition}
        onRetry={() => setAttempt((value) => value + 1)}
        onBack={onBack}
        onOpenSettings={onOpenSettings}
        signedInUsername={session.username}
        onOpenProfile={onOpenProfile}
      />
      {shareOpen ? <ShareTripDialog trip={trip} onClose={() => setShareOpen(false)} /> : null}
    </>
  )
}

function accountTripIdFromUrl(): string | null {
  const value = new URLSearchParams(window.location.search).get('trip')?.trim()
  return value && /^\d+$/.test(value) ? value : null
}

function AccountRoute({ unitSystem, followLatestPosition, showLivePositionInfo, onOpenSettings }: { unitSystem: UnitSystem; followLatestPosition: boolean; showLivePositionInfo: boolean; onOpenSettings: () => void }) {
  const storedSession = useMemo(readAuthSession, [])
  const [state, setState] = useState<AccountState>(storedSession ? { kind: 'checking' } : { kind: 'signed-out' })
  const [accessView, setAccessView] = useState<'login' | 'register' | 'forgot'>('login')
  const [accessPending, setAccessPending] = useState(false)
  const [accessMessage, setAccessMessage] = useState<{ kind: 'error' | 'notice'; text: string } | null>(null)
  const [selectedTripId, setSelectedTripId] = useState(accountTripIdFromUrl)
  const [selectedTripIds, setSelectedTripIds] = useState<string[]>(() => {
    const tripId = accountTripIdFromUrl()
    return tripId ? [tripId] : []
  })
  const previousSelectedTripIdRef = useRef(selectedTripId)
  const [profileOpen, setProfileOpen] = useState(false)
  const [shareTrip, setShareTrip] = useState<AccountTrip | null>(null)
  const [editTrip, setEditTrip] = useState<AccountTrip | null>(null)
  const [tripFilters, setTripFilters] = useState<AccountTripFilters>(() => ({ ...DEFAULT_ACCOUNT_TRIP_FILTERS }))
  const [newTripIds, setNewTripIds] = useState<string[]>([])
  const [lastMapViewport, setLastMapViewport] = useState<MapViewport>()
  const tripLoadControllerRef = useRef<AbortController | null>(null)
  const knownTripIdsRef = useRef<Set<string> | null>(null)
  const activeTripListSessionRef = useRef<string | null>(null)
  const newTripAudioRef = useRef<HTMLAudioElement | null>(null)
  const newTripAnimationTimerRef = useRef<number | null>(null)
  const api = useMemo(createAccountApi, [])
  const desktopWorkspace = useDesktopTripWorkspace()

  const getNewTripAudio = useCallback(() => {
    if (!newTripAudioRef.current) {
      newTripAudioRef.current = new Audio(newTripSoundUrl)
      newTripAudioRef.current.preload = 'auto'
    }
    return newTripAudioRef.current
  }, [])

  const playNewTripSound = useCallback(() => {
    const audio = getNewTripAudio()
    audio.muted = false
    audio.currentTime = 0
    void audio.play().catch(() => {
      // Some browsers block playback until the page has received a user gesture.
    })
  }, [getNewTripAudio])

  useEffect(() => {
    const unlockAudio = () => {
      const audio = getNewTripAudio()
      audio.muted = true
      void audio.play().then(() => {
        audio.pause()
        audio.currentTime = 0
        audio.muted = false
      }).catch(() => {
        audio.muted = false
      })
    }
    document.addEventListener('pointerdown', unlockAudio, { once: true })
    return () => document.removeEventListener('pointerdown', unlockAudio)
  }, [getNewTripAudio])

  useEffect(() => () => {
    if (newTripAnimationTimerRef.current !== null) window.clearTimeout(newTripAnimationTimerRef.current)
  }, [])

  const animateNewTrips = useCallback((tripIds: string[]) => {
    if (!tripIds.length) return
    if (newTripAnimationTimerRef.current !== null) window.clearTimeout(newTripAnimationTimerRef.current)
    setNewTripIds(tripIds)
    newTripAnimationTimerRef.current = window.setTimeout(() => {
      newTripAnimationTimerRef.current = null
      setNewTripIds([])
    }, 1_200)
  }, [])

  useEffect(() => {
    const handlePopState = () => {
      const tripId = accountTripIdFromUrl()
      setSelectedTripId(tripId)
      setSelectedTripIds(tripId ? [tripId] : [])
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  useEffect(() => {
    scrollToPageTop()
  }, [accessView, selectedTripId, state.kind])

  const loadTrips = useCallback((session: AuthSession, filters: AccountTripFilters) => {
    tripLoadControllerRef.current?.abort()
    const controller = new AbortController()
    tripLoadControllerRef.current = controller
    activeTripListSessionRef.current = session.sid
    knownTripIdsRef.current = null
    setNewTripIds([])
    setState({ kind: 'trips', session, trips: [], loading: true, loadingEarlier: false, hasEarlier: false })
    void api.listOwnTrips(session, { limit: TRIP_PAGE_SIZE, filters }, controller.signal).then((page) => {
      if (tripLoadControllerRef.current === controller) tripLoadControllerRef.current = null
      if (activeTripListSessionRef.current === session.sid) {
        knownTripIdsRef.current = new Set(page.trips.map((trip) => trip.id))
      }
      setState({ kind: 'trips', session, trips: page.trips, loading: false, loadingEarlier: false, hasEarlier: page.hasEarlier })
    }).catch((error: unknown) => {
      if (error instanceof DOMException && error.name === 'AbortError') return
      if (tripLoadControllerRef.current === controller) tripLoadControllerRef.current = null
      if (error instanceof AccountApiError && error.code === 'expired-session') {
        clearAuthSession()
        setState({ kind: 'signed-out', error: messageForAccountError(error) })
        return
      }
      setState({ kind: 'trips', session, trips: [], loading: false, loadingEarlier: false, hasEarlier: false, error: messageForAccountError(error) })
    })
    return () => controller.abort()
  }, [api])

  const refreshTripsInBackground = useCallback((session: AuthSession, loadedTripCount: number, filters: AccountTripFilters) => {
    const controller = new AbortController()
    const limit = Math.max(loadedTripCount, TRIP_PAGE_SIZE)
    void api.listOwnTrips(session, { limit, filters }, controller.signal).then((page) => {
      if (activeTripListSessionRef.current !== session.sid) return
      const knownTripIds = knownTripIdsRef.current
      const addedTripIds = knownTripIds === null
        ? []
        : page.trips.filter((trip) => !knownTripIds.has(trip.id)).map((trip) => trip.id)
      const hasNewTrip = knownTripIds !== null && includesNewTrip(knownTripIds, page.trips)
      knownTripIdsRef.current = new Set(page.trips.map((trip) => trip.id))
      if (hasNewTrip) {
        playNewTripSound()
        animateNewTrips(addedTripIds)
      }
      setState((current) => {
        if (current.kind !== 'trips' || current.session.sid !== session.sid) return current
        return applyTripListRefresh(current, page)
      })
    }).catch((error: unknown) => {
      if (error instanceof DOMException && error.name === 'AbortError') return
      if (error instanceof AccountApiError && error.code === 'expired-session') {
        clearAuthSession()
        setState({ kind: 'signed-out', error: messageForAccountError(error) })
      }
    })
    return () => controller.abort()
  }, [animateNewTrips, api, playNewTripSound])

  const tripListSessionId = state.kind === 'trips' ? state.session.sid : null
  useEffect(() => {
    const previousTripId = previousSelectedTripIdRef.current
    previousSelectedTripIdRef.current = selectedTripId
    if (previousTripId === null || selectedTripId !== null || state.kind !== 'trips') return
    return refreshTripsInBackground(state.session, state.trips.length, tripFilters)
  }, [refreshTripsInBackground, selectedTripId, state.kind, state.kind === 'trips' ? state.trips.length : 0, tripFilters, tripListSessionId])

  const visibleTripCount = state.kind === 'trips' ? state.trips.length : 0
  useEffect(() => {
    if ((selectedTripId !== null && !desktopWorkspace) || state.kind !== 'trips' || state.loading) return
    let cancelRefresh: (() => void) | undefined
    const timer = window.setInterval(() => {
      cancelRefresh?.()
      cancelRefresh = refreshTripsInBackground(state.session, visibleTripCount, tripFilters)
    }, TRIP_LIST_POLL_INTERVAL_MS)
    return () => {
      window.clearInterval(timer)
      cancelRefresh?.()
    }
  }, [desktopWorkspace, refreshTripsInBackground, selectedTripId, state.kind, state.kind === 'trips' ? state.loading : false, tripFilters, tripListSessionId, visibleTripCount])

  useEffect(() => {
    if (!storedSession) return
    const controller = new AbortController()
    void api.validateSession(storedSession, controller.signal).then((session) => {
      storeAuthSession(session)
      loadTrips(session, tripFilters)
    }).catch((error: unknown) => {
      if (error instanceof DOMException && error.name === 'AbortError') return
      if (error instanceof AccountApiError && error.code === 'expired-session') clearAuthSession()
      setState({ kind: 'signed-out', error: messageForAccountError(error) })
    })
    return () => controller.abort()
  }, [api, loadTrips, storedSession])

  const login = (credentials: LoginCredentials) => {
    const controller = new AbortController()
    setAccessPending(true)
    setAccessMessage(null)
    setState({ kind: 'signed-out' })
    void api.login(credentials, controller.signal).then((session) => {
      storeAuthSession(session)
      setAccessPending(false)
      loadTrips(session, tripFilters)
    }).catch((error: unknown) => {
      if (error instanceof DOMException && error.name === 'AbortError') return
      setAccessPending(false)
      setState({ kind: 'signed-out', error: messageForAccountError(error) })
    })
  }

  const register = (details: RegistrationDetails) => {
    const controller = new AbortController()
    setAccessPending(true)
    setAccessMessage(null)
    void api.register(details, controller.signal).then(() => {
      setAccessPending(false)
      setAccessView('login')
      setState({ kind: 'signed-out' })
      setAccessMessage({ kind: 'notice', text: 'Your account has been created. You can now sign in.' })
    }).catch((error: unknown) => {
      if (error instanceof DOMException && error.name === 'AbortError') return
      setAccessPending(false)
      setAccessMessage({ kind: 'error', text: messageForAccountError(error) })
    })
  }

  const requestPasswordReset = (usernameOrEmail: string) => {
    const controller = new AbortController()
    setAccessPending(true)
    setAccessMessage(null)
    void api.requestPasswordReset(usernameOrEmail, controller.signal).then(() => {
      setAccessPending(false)
      setAccessMessage({
        kind: 'notice',
        text: 'If the account can be found, recovery instructions will be sent to its registered email address.',
      })
    }).catch((error: unknown) => {
      if (error instanceof DOMException && error.name === 'AbortError') return
      setAccessPending(false)
      setAccessMessage({ kind: 'error', text: messageForAccountError(error) })
    })
  }

  const loadEarlierTrips = () => {
    if (state.kind !== 'trips' || state.loadingEarlier || !state.hasEarlier) return
    const beforeTripId = state.trips.at(-1)?.id
    if (!beforeTripId) return
    const session = state.session
    const controller = new AbortController()
    setState((current) => current.kind === 'trips'
      ? { ...current, loadingEarlier: true, earlierError: undefined }
      : current)
    void api.listOwnTrips(session, { beforeTripId, limit: TRIP_PAGE_SIZE, filters: tripFilters }, controller.signal).then((page) => {
      if (activeTripListSessionRef.current === session.sid) {
        const knownTripIds = knownTripIdsRef.current ?? new Set<string>()
        page.trips.forEach((trip) => knownTripIds.add(trip.id))
        knownTripIdsRef.current = knownTripIds
      }
      setState((current) => {
        if (current.kind !== 'trips' || current.session.sid !== session.sid) return current
        const knownIds = new Set(current.trips.map((trip) => trip.id))
        const earlierTrips = page.trips.filter((trip) => !knownIds.has(trip.id))
        return {
          ...current,
          trips: [...current.trips, ...earlierTrips],
          loadingEarlier: false,
          hasEarlier: page.hasEarlier,
          earlierError: undefined,
        }
      })
    }).catch((error: unknown) => {
      if (error instanceof DOMException && error.name === 'AbortError') return
      if (error instanceof AccountApiError && error.code === 'expired-session') {
        clearAuthSession()
        setState({ kind: 'signed-out', error: messageForAccountError(error) })
        return
      }
      setState((current) => current.kind === 'trips'
        ? { ...current, loadingEarlier: false, earlierError: messageForAccountError(error) }
        : current)
    })
  }

  const logout = () => {
    clearAuthSession()
    activeTripListSessionRef.current = null
    knownTripIdsRef.current = null
    setProfileOpen(false)
    const url = new URL(window.location.href)
    url.searchParams.delete('trip')
    window.history.replaceState({}, '', url)
    setSelectedTripId(null)
    setSelectedTripIds([])
    setAccessView('login')
    setAccessMessage(null)
    setTripFilters({ ...DEFAULT_ACCOUNT_TRIP_FILTERS })
    setState({ kind: 'signed-out' })
  }

  const updateOwnTrip = async (trip: AccountTrip, update: AccountTripUpdate) => {
    if (state.kind !== 'trips' || trip.ownerName !== state.session.username) {
      throw new AccountApiError('request-rejected', 'Only your own trips can be edited.')
    }
    await api.updateTrip(state.session, trip.id, update)
    setState((current) => current.kind === 'trips'
      ? {
          ...current,
          trips: current.trips.map((candidate) => candidate.id === trip.id
            ? { ...candidate, description: update.description || undefined, visibility: update.visibility }
            : candidate),
        }
      : current)
  }

  const deleteOwnTrip = async (trip: AccountTrip) => {
    if (state.kind !== 'trips' || trip.ownerName !== state.session.username) {
      throw new AccountApiError('request-rejected', 'Only your own trips can be deleted.')
    }
    await api.deleteTrip(state.session, trip.id)
    setState((current) => current.kind === 'trips'
      ? { ...current, trips: current.trips.filter((candidate) => candidate.id !== trip.id) }
      : current)
    if (selectedTripIds.includes(trip.id)) {
      const remaining = selectedTripIds.filter((tripId) => tripId !== trip.id)
      setSelectedTripIds(remaining)
      const url = new URL(window.location.href)
      if (remaining.length) url.searchParams.set('trip', remaining[0]!)
      else url.searchParams.delete('trip')
      window.history.replaceState({}, '', url)
      setSelectedTripId(remaining[0] ?? null)
    }
  }

  const endOwnTrip = async (trip: AccountTrip) => {
    if (state.kind !== 'trips' || trip.ownerName !== state.session.username || trip.status === 'ended') {
      throw new AccountApiError('request-rejected', 'Only your own active trips can be ended.')
    }
    await api.endTrip(state.session, trip.id)
    const endedAt = new Date().toISOString()
    setState((current) => current.kind === 'trips'
      ? {
          ...current,
          trips: current.trips.map((candidate) => candidate.id === trip.id
            ? { ...candidate, status: 'ended', endedAt }
            : candidate),
        }
      : current)
  }

  if (state.kind === 'checking') {
    return selectedTripId ? <SharedTripView state={{ kind: 'loading' }} /> : <AccountLoadingView />
  }
  if (state.kind === 'signed-out') {
    if (accessView === 'register') {
      return (
        <RegisterView
          pending={accessPending}
          error={accessMessage?.kind === 'error' ? accessMessage.text : undefined}
          onSubmit={register}
          onBack={() => { setAccessView('login'); setAccessMessage(null); setState({ kind: 'signed-out' }) }}
        />
      )
    }
    if (accessView === 'forgot') {
      return (
        <ForgotPasswordView
          pending={accessPending}
          error={accessMessage?.kind === 'error' ? accessMessage.text : undefined}
          success={accessMessage?.kind === 'notice' ? accessMessage.text : undefined}
          onSubmit={requestPasswordReset}
          onBack={() => { setAccessView('login'); setAccessMessage(null); setState({ kind: 'signed-out' }) }}
        />
      )
    }
    return (
      <LoginView
        pending={accessPending}
        error={state.error}
        notice={accessMessage?.kind === 'notice' ? accessMessage.text : undefined}
        showDemoCredentials={import.meta.env.VITE_API_MODE !== 'legacy'}
        onSubmit={login}
        onRegister={() => { setAccessView('register'); setAccessMessage(null); setState({ kind: 'signed-out' }) }}
        onForgotPassword={() => { setAccessView('forgot'); setAccessMessage(null); setState({ kind: 'signed-out' }) }}
      />
    )
  }
  const selectedTrip = selectedTripId
    ? state.trips.find((trip) => trip.id === selectedTripId)
    : undefined
  const openAccountTrip = (trip: AccountTrip) => {
    const url = new URL(window.location.href)
    url.searchParams.set('trip', trip.id)
    window.history.pushState({ ...window.history.state, mapapalAccountTrip: trip.id }, '', url)
    setSelectedTripId(trip.id)
    setSelectedTripIds([trip.id])
  }
  const closeAccountTrip = () => {
    const url = new URL(window.location.href)
    url.searchParams.delete('trip')
    window.history.replaceState({}, '', url)
    setSelectedTripId(null)
    setSelectedTripIds([])
  }

  const showSingleSelectedTrip = (tripId: string | null) => {
    const url = new URL(window.location.href)
    if (tripId) url.searchParams.set('trip', tripId)
    else url.searchParams.delete('trip')
    window.history.replaceState({}, '', url)
    setSelectedTripId(tripId)
  }

  const toggleTripOnMap = (trip: AccountTrip) => {
    const selected = selectedTripIds.includes(trip.id)
    const next = selected
      ? selectedTripIds.filter((tripId) => tripId !== trip.id)
      : [...selectedTripIds, trip.id]
    setSelectedTripIds(next)
    showSingleSelectedTrip(next[0] ?? null)
  }

  const clearTripMapSelection = () => {
    setSelectedTripIds([])
    showSingleSelectedTrip(null)
  }

  const applyTripFilters = (filters: AccountTripFilters) => {
    clearTripMapSelection()
    setLastMapViewport(undefined)
    setTripFilters(filters)
    loadTrips(state.session, filters)
  }

  if (desktopWorkspace) {
    const comparisonTrips = selectedTripIds.flatMap((tripId) => {
      const trip = state.trips.find((candidate) => candidate.id === tripId)
      return trip ? [trip] : []
    })
    const desktopDetail = selectedTrip ? (
      <AccountTripRoute
        embedded
        api={api}
        session={state.session}
        trip={selectedTrip}
        unitSystem={unitSystem}
        followLatestPosition={followLatestPosition}
        showLivePositionInfo={showLivePositionInfo}
        onOpenSettings={onOpenSettings}
        onOpenProfile={() => setProfileOpen(true)}
        onEdit={() => setEditTrip(selectedTrip)}
        onBack={closeAccountTrip}
        comparisonTrips={comparisonTrips}
        onClearComparison={clearTripMapSelection}
        onRemoveComparisonTrip={toggleTripOnMap}
        onMapViewportChange={setLastMapViewport}
      />
    ) : selectedTripId ? (
      <SharedTripView
        embedded
        state={state.loading ? { kind: 'loading' } : { kind: 'not-found' }}
        onBack={closeAccountTrip}
      />
    ) : <DesktopOverviewMap initialViewport={lastMapViewport} />

    return (
      <>
        <TripListView
          session={state.session}
          trips={state.trips}
          loading={state.loading}
          loadingEarlier={state.loadingEarlier}
          hasEarlier={state.hasEarlier}
          earlierError={state.earlierError}
          error={state.error}
          filters={tripFilters}
          onApplyFilters={applyTripFilters}
          onRetry={() => loadTrips(state.session, tripFilters)}
          onLoadEarlier={loadEarlierTrips}
          unitSystem={unitSystem}
          onOpenSettings={onOpenSettings}
          onOpenProfile={() => setProfileOpen(true)}
          onShareTrip={(trip) => {
            if (trip.ownerName === state.session.username) setShareTrip(trip)
          }}
          onEditTrip={(trip) => {
            if (trip.ownerName === state.session.username) setEditTrip(trip)
          }}
          onOpenTrip={openAccountTrip}
          selectedTripId={selectedTripId}
          selectedTripIds={selectedTripIds}
          newTripIds={newTripIds}
          onToggleTripSelection={toggleTripOnMap}
          desktopDetail={desktopDetail}
        />
        <ProfileDialog open={profileOpen} session={state.session} api={api} onLogout={logout} onClose={() => setProfileOpen(false)} />
        {shareTrip ? <ShareTripDialog trip={shareTrip} onClose={() => setShareTrip(null)} /> : null}
        {editTrip ? (
          <EditTripDialog
            key={editTrip.id}
            trip={editTrip}
            onSave={(update) => updateOwnTrip(editTrip, update)}
            onEnd={editTrip.status !== 'ended' ? () => endOwnTrip(editTrip) : undefined}
            onDelete={() => deleteOwnTrip(editTrip)}
            onClose={() => setEditTrip(null)}
          />
        ) : null}
      </>
    )
  }
  if (selectedTripId && state.loading) return <SharedTripView state={{ kind: 'loading' }} />
  if (selectedTrip) {
    return (
      <>
        <AccountTripRoute
          api={api}
          session={state.session}
          trip={selectedTrip}
          unitSystem={unitSystem}
          followLatestPosition={followLatestPosition}
          showLivePositionInfo={showLivePositionInfo}
          onOpenSettings={onOpenSettings}
          onOpenProfile={() => setProfileOpen(true)}
          onEdit={() => setEditTrip(selectedTrip)}
          onBack={() => {
            if (window.history.state?.mapapalAccountTrip) {
              window.history.back()
            } else {
              const url = new URL(window.location.href)
              url.searchParams.delete('trip')
              window.history.replaceState({}, '', url)
              setSelectedTripId(null)
            }
          }}
        />
        <ProfileDialog open={profileOpen} session={state.session} api={api} onLogout={logout} onClose={() => setProfileOpen(false)} />
        {editTrip ? (
          <EditTripDialog
            key={editTrip.id}
            trip={editTrip}
            onSave={(update) => updateOwnTrip(editTrip, update)}
            onEnd={editTrip.status !== 'ended' ? () => endOwnTrip(editTrip) : undefined}
            onDelete={() => deleteOwnTrip(editTrip)}
            onClose={() => setEditTrip(null)}
          />
        ) : null}
      </>
    )
  }
  return (
    <>
      <TripListView
        session={state.session}
        trips={state.trips}
        loading={state.loading}
        loadingEarlier={state.loadingEarlier}
        hasEarlier={state.hasEarlier}
        earlierError={state.earlierError}
        error={state.error}
        filters={tripFilters}
        onApplyFilters={applyTripFilters}
        onRetry={() => loadTrips(state.session, tripFilters)}
        onLoadEarlier={loadEarlierTrips}
        unitSystem={unitSystem}
        onOpenSettings={onOpenSettings}
        onOpenProfile={() => setProfileOpen(true)}
        onShareTrip={(trip) => {
          if (trip.ownerName === state.session.username) setShareTrip(trip)
        }}
        onEditTrip={(trip) => {
          if (trip.ownerName === state.session.username) setEditTrip(trip)
        }}
        onOpenTrip={(trip) => {
          openAccountTrip(trip)
        }}
        newTripIds={newTripIds}
      />
      <ProfileDialog open={profileOpen} session={state.session} api={api} onLogout={logout} onClose={() => setProfileOpen(false)} />
      {shareTrip ? <ShareTripDialog trip={shareTrip} onClose={() => setShareTrip(null)} /> : null}
      {editTrip ? (
        <EditTripDialog
          key={editTrip.id}
          trip={editTrip}
          onSave={(update) => updateOwnTrip(editTrip, update)}
          onEnd={editTrip.status !== 'ended' ? () => endOwnTrip(editTrip) : undefined}
          onDelete={() => deleteOwnTrip(editTrip)}
          onClose={() => setEditTrip(null)}
        />
      ) : null}
    </>
  )
}

export function App() {
  const publicPage = publicPageFromPath(window.location.pathname)
  const [link] = useState(() => publicPage ? null : initialShareLink())
  const [unitSystem, setUnitSystem] = useState(readUnitSystem)
  const [followLatestPosition, setFollowLatestPosition] = useState(readFollowLatestPosition)
  const [showLivePositionInfo, setShowLivePositionInfo] = useState(readShowLivePositionInfo)
  const [settingsOpen, setSettingsOpen] = useState(false)

  useEffect(() => {
    scrollToPageTop()
  }, [link, publicPage])

  useEffect(() => {
    const viewingSharedTrip = link !== null
    const publicMeta = publicPage ? PUBLIC_PAGE_META[publicPage] : null
    const title = publicMeta?.title ?? (viewingSharedTrip
      ? 'Shared trip – Mapapal'
      : 'Mapapal – Live GPS trip sharing for friends and family')
    const description = publicMeta?.description ?? (viewingSharedTrip
      ? 'Follow a trip shared privately with you through Mapapal.'
      : 'Share live GPS trips with friends and family. Start a journey in the Mapapal app and let anyone you choose follow safely from their browser.')
    const canonicalUrl = new URL(publicPage ? `${publicPage}/` : '', 'https://www.mapapal.com/').href

    document.title = title
    updateMetaContent(
      'meta[name="robots"]',
      viewingSharedTrip ? 'noindex,nofollow,noarchive' : 'index,follow,max-image-preview:large',
    )
    updateMetaContent('meta[name="description"]', description)
    updateMetaContent('meta[property="og:title"]', title)
    updateMetaContent('meta[property="og:description"]', description)
    updateMetaContent('meta[name="twitter:title"]', title)
    updateMetaContent('meta[name="twitter:description"]', description)
    updateCanonicalUrl(canonicalUrl)
  }, [link, publicPage])

  return (
    <>
      {publicPage
        ? <PublicInformationPage page={publicPage} />
        : link
          ? <SharedTripRoute link={link} unitSystem={unitSystem} followLatestPosition={followLatestPosition} showLivePositionInfo={showLivePositionInfo} onOpenSettings={() => setSettingsOpen(true)} />
          : <AccountRoute unitSystem={unitSystem} followLatestPosition={followLatestPosition} showLivePositionInfo={showLivePositionInfo} onOpenSettings={() => setSettingsOpen(true)} />}
      <SiteFooter />
      {publicPage ? null : <SettingsDialog
        open={settingsOpen}
        unitSystem={unitSystem}
        followLatestPosition={followLatestPosition}
        showLivePositionInfo={showLivePositionInfo}
        onChange={(value) => {
          storeUnitSystem(value)
          setUnitSystem(value)
        }}
        onFollowLatestPositionChange={(enabled) => {
          storeFollowLatestPosition(enabled)
          setFollowLatestPosition(enabled)
        }}
        onShowLivePositionInfoChange={(enabled) => {
          storeShowLivePositionInfo(enabled)
          setShowLivePositionInfo(enabled)
        }}
        onClose={() => setSettingsOpen(false)}
      />}
    </>
  )
}
